// src/pages/shop/ShopWaiterOrder.jsx
//
// Карточка заказа. Просмотр позиций со статусом готовки, добавление блюд
// (в один клик), запрос на удаление (кухня решает), закрытие заказа (=продажа)
// и отмена. Закрытие идёт через бэк /close — обычная продажа с этими позициями.

import { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { cafeOrders as cafeApi, products as productsApi, debtors as debtorsApi } from '../../api';
import { useToast } from '../../hooks/useToast';
import { useMyCurrency, useMyShop } from '../../hooks/useMyShop';
import { useAuth } from '../../hooks/useAuth';
import { isModuleOn } from '../../config/modules';
import { formatMoney } from '../../data/currencies';
import { errorMessage } from '../../utils/errorMessage';
import { shareQR, saveQR } from '../../utils/qrShare';
import Icon from '../../components/Icon';
import ScanButton from '../../components/ScanButton';

function cookPill(t, item) {
  if (item.removalRequested) return { label: t('waiter.stRemoval'), color: 'var(--danger)', bg: 'rgba(220,50,50,.12)' };
  const map = {
    done:    { label: t('waiter.stReady'),    color: 'var(--success)', bg: 'rgba(46,160,67,.12)' },
    cooking: { label: t('waiter.stCooking'),  color: 'var(--warning, #b8860b)', bg: 'rgba(184,134,11,.12)' },
    pending: { label: t('waiter.stWaiting'),  color: 'var(--fg-muted)', bg: 'var(--bg-subtle)' },
  };
  return map[item.cookStatus] || map.pending;
}

export default function ShopWaiterOrder() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { show, ToastView } = useToast();
  const currency = useMyCurrency();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');

  // Лист закрытия (скидка / долг)
  const shop = useMyShop();
  const { isOwner, can } = useAuth();
  const canDiscount = isOwner || can('discounts');
  const debtEnabled = isModuleOn(shop, 'retailDebt');

  const [showClose, setShowClose] = useState(false);
  const [discType, setDiscType] = useState(null);   // 'percent' | 'amount' | null
  const [discValue, setDiscValue] = useState('');
  const [debtOn, setDebtOn] = useState(false);
  const [debtors, setDebtors] = useState([]);
  const [debtorId, setDebtorId] = useState('');
  const [paidNow, setPaidNow] = useState('');

  // QR-чек после закрытия (если начислены баллы)
  const [closeResult, setCloseResult] = useState(null); // { token, points }
  const [sharing, setSharing] = useState(false);
  const qrRef = useRef(null);

  const load = async () => {
    try { setOrder(await cafeApi.get(id)); }
    catch (e) { show(errorMessage(e, t), 'error'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const openAdd = async () => {
    setAdding(true);
    if (products.length === 0) {
      try { setProducts(await productsApi.list()); } catch { /* noop */ }
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = q ? products.filter(p => (p.name || '').toLowerCase().includes(q)) : products;
    return list.slice(0, 40);
  }, [products, search]);

  const addItem = async (p) => {
    setBusy(true);
    try {
      await cafeApi.update(id, { addItems: [{
        productId: p.id, name: p.name, qty: 1, unit: p.unit || 'шт',
        salePrice: Number(p.salePrice) || 0, costPrice: Number(p.costPrice) || 0,
      }] });
      await load();
      show(t('cashier.added', { name: p.name }));
    } catch (e) { show(errorMessage(e, t), 'error'); }
    finally { setBusy(false); }
  };

  const handleScan = async (code) => {
    try {
      const r = await productsApi.byBarcode(code);
      if (r?.product) await addItem(r.product);
      else show(t('marking.notOnStock'), 'error');
    } catch { show(t('marking.notOnStock'), 'error'); }
  };

  const removeItem = async (item) => {
    setBusy(true);
    try {
      await cafeApi.update(id, { removeItemIds: [item.id] });
      await load();
    } catch (e) { show(errorMessage(e, t), 'error'); }
    finally { setBusy(false); }
  };

  const openClose = async () => {
    setDiscType(null); setDiscValue(''); setDebtOn(false); setDebtorId(''); setPaidNow('');
    setShowClose(true);
    if (debtEnabled && debtors.length === 0) {
      try { const r = await debtorsApi.list(); setDebtors(Array.isArray(r) ? r : (r.items || [])); } catch { /* noop */ }
    }
  };

  const submitClose = async () => {
    const payload = {};
    const v = Number(String(discValue).replace(',', '.')) || 0;
    if (canDiscount && discType && v > 0) { payload.discountType = discType; payload.discountValue = v; }
    if (debtOn) {
      if (!debtorId) { show(t('waiter.errNoDebtor'), 'error'); return; }
      payload.counterpartyId = debtorId;
      const pn = Number(String(paidNow).replace(',', '.'));
      if (!Number.isNaN(pn) && paidNow !== '') payload.paidNow = pn;
    }
    const notReady = order.items.filter(i => i.cookStatus !== 'done' && !i.removalRequested).length;
    if (notReady > 0 && !window.confirm(t('waiter.confirmNotReady', { count: notReady }))) return;
    setBusy(true);
    try {
      const res = await cafeApi.close(id, payload);
      if (res && res.loyaltyEnabled && res.pointsEarned > 0 && res.token) {
        setShowClose(false);
        setCloseResult({ token: res.token, points: res.pointsEarned });
      } else {
        show(t('waiter.closed'));
        navigate('/waiter', { replace: true });
      }
    } catch (e) { show(errorMessage(e, t), 'error'); }
    finally { setBusy(false); }
  };

  const shareReceipt = async () => {
    const svgEl = qrRef.current?.querySelector('svg');
    if (!svgEl || sharing) return;
    setSharing(true);
    try {
      const r = await shareQR({ svgEl, filename: `alsatuu-receipt-${closeResult.token.slice(0, 6)}.png`, title: 'Alsatuu', text: t('cashier.shareReceiptText', { shop: shop?.name || '', points: closeResult.points }) });
      if (r && r.method === 'download-fallback') show(t('cashier.qrSavedToDownloads'), 'success');
    } catch { show(t('cashier.shareError'), 'error'); }
    setSharing(false);
  };
  const saveReceipt = async () => {
    const svgEl = qrRef.current?.querySelector('svg');
    if (!svgEl || sharing) return;
    setSharing(true);
    try { await saveQR({ svgEl, filename: `alsatuu-receipt-${closeResult.token.slice(0, 6)}.png` }); show(t('cashier.qrSavedSuccess'), 'success'); }
    catch { show(t('cashier.shareError'), 'error'); }
    setSharing(false);
  };

  const cancelOrder = async () => {
    if (!window.confirm(t('waiter.confirmCancel'))) return;
    setBusy(true);
    try {
      await cafeApi.cancel(id);
      show(t('waiter.cancelled'));
      navigate('/waiter', { replace: true });
    } catch (e) { show(errorMessage(e, t), 'error'); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="page"><div className="loader" style={{ padding: 64 }}><div className="spinner" /></div></div>;
  if (!order) return <div className="page" style={{ padding: 20 }}>{t('waiter.notFound')}</div>;

  if (closeResult) {
    return (
      <div className="page" style={{ padding: 20, display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        {ToastView}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
          <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'var(--success)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12, color: '#fff' }}>
            <Icon name="check" size={30} stroke={2.5} />
          </div>
          <h2 style={{ fontSize: 20, fontWeight: 800, marginBottom: 4 }}>{t('waiter.closed')}</h2>
          <p style={{ fontSize: 14, color: 'var(--brand-accent)', fontWeight: 700 }}>{t('waiter.pointsEarned', { points: closeResult.points })}</p>
          <div ref={qrRef} style={{ marginTop: 24, padding: 20, background: '#fff', borderRadius: 'var(--r-md)', boxShadow: 'var(--shadow-md)' }}>
            <QRCodeSVG value={JSON.stringify({ shopId: shop?.id, token: closeResult.token })} size={200} level="M" />
          </div>
          <p style={{ fontSize: 13, color: 'var(--fg-muted)', marginTop: 16, maxWidth: 280 }}>{t('cashier.qrHint', { points: closeResult.points })}</p>
          <div style={{ display: 'flex', gap: 8, marginTop: 16, width: '100%', maxWidth: 320 }}>
            <button className="btn btn-secondary" onClick={saveReceipt} disabled={sharing} style={{ flex: 1 }}><Icon name="download" size={16} /> {t('cashier.saveQR')}</button>
            <button className="btn btn-primary" onClick={shareReceipt} disabled={sharing} style={{ flex: 1 }}><Icon name="share" size={16} /> {t('cashier.shareQR')}</button>
          </div>
        </div>
        <button className="btn btn-primary btn-lg" onClick={() => navigate('/waiter', { replace: true })}>{t('waiter.qrDone')}</button>
      </div>
    );
  }

  const isOpen = order.status === 'open';

  // Расчёт для листа закрытия
  const base = order.total;
  const dv = Number(String(discValue).replace(',', '.')) || 0;
  const disc = (canDiscount && discType && dv > 0)
    ? (discType === 'percent' ? (base * Math.min(dv, 100)) / 100 : Math.min(dv, base))
    : 0;
  const finalTotal = Math.max(0, Math.round((base - disc) * 100) / 100);
  const pnParsed = Number(String(paidNow).replace(',', '.'));
  const paidVal = (paidNow === '' || Number.isNaN(pnParsed)) ? finalTotal : pnParsed;
  const credit = debtOn ? Math.max(0, Math.round((finalTotal - paidVal) * 100) / 100) : 0;

  // Блюда, которые ещё не «готово» (и не помечены на удаление)
  const notReadyCount = order.items.filter(i => i.cookStatus !== 'done' && !i.removalRequested).length;

  return (
    <div className="page" style={{ paddingBottom: 120 }}>
      {ToastView}
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate('/waiter')}><Icon name="chevronLeft" size={18} /></button>
        <h1 className="page-title">{t('waiter.tableShort')} {order.tableLabel} · №{order.number}</h1>
      </div>

      <div style={{ padding: '0 20px' }}>
        {/* Позиции */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {order.items.map(it => {
            const s = cookPill(t, it);
            return (
              <div key={it.id} className="card" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>{it.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--fg-muted)' }}>
                    {it.qty} × {formatMoney(it.salePrice, currency)}
                  </div>
                </div>
                <span style={{ fontSize: 11, fontWeight: 700, color: s.color, background: s.bg, padding: '2px 8px', borderRadius: 999, flexShrink: 0, whiteSpace: 'nowrap' }}>{s.label}</span>
                {isOpen && (
                  <button className="btn btn-ghost" onClick={() => removeItem(it)} disabled={busy} style={{ width: 'auto', flexShrink: 0, padding: '4px 6px', minHeight: 0, color: 'var(--danger)' }}>
                    <Icon name="x" size={16} />
                  </button>
                )}
              </div>
            );
          })}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '14px 0' }}>
          <span style={{ fontSize: 15, fontWeight: 700 }}>{t('wholesale.total')}</span>
          <span style={{ fontSize: 20, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{formatMoney(order.total, currency)}</span>
        </div>

        {order.note && (
          <div style={{ padding: 10, background: 'var(--bg-subtle)', borderRadius: 'var(--r-sm)', fontSize: 13, marginBottom: 12 }}>{order.note}</div>
        )}

        {/* Добавление блюд */}
        {isOpen && (
          adding ? (
            <div style={{ marginTop: 4 }}>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <input className="input" value={search} onChange={e => setSearch(e.target.value)} placeholder={t('cashier.searchPlaceholder')} style={{ flex: 1 }} />
                <ScanButton raw onScan={handleScan} size="md" />
                <button className="btn btn-ghost" onClick={() => setAdding(false)} style={{ width: 'auto', flexShrink: 0, padding: '0 10px' }}><Icon name="x" size={16} /></button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 260, overflowY: 'auto' }}>
                {filtered.map(p => (
                  <button key={p.id} onClick={() => addItem(p)} disabled={busy} className="card"
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', textAlign: 'left', width: '100%', border: '1px solid var(--border)' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600 }}>{p.name}</div>
                      <div style={{ fontSize: 12, color: 'var(--fg-muted)' }}>{formatMoney(p.salePrice, currency)}</div>
                    </div>
                    <Icon name="plus" size={16} />
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <button className="btn btn-secondary" onClick={openAdd} style={{ width: '100%' }}>
              <Icon name="plus" size={16} /> {t('waiter.addItems')}
            </button>
          )
        )}
      </div>

      {/* Действия снизу */}
      {isOpen && (
        <div style={{ position: 'sticky', bottom: 0, background: 'var(--bg-elevated)', borderTop: '1px solid var(--border)', padding: '12px 20px', marginTop: 16, display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost" onClick={cancelOrder} disabled={busy} style={{ width: 'auto', flexShrink: 0, color: 'var(--danger)' }}>{t('waiter.cancelOrder')}</button>
          <button className="btn btn-primary" onClick={openClose} disabled={busy || order.items.length === 0} style={{ flex: 1 }}>
            {t('waiter.closeBtn')}
          </button>
        </div>
      )}

      {/* Лист закрытия: скидка / долг */}
      {showClose && (
        <div onClick={() => !busy && setShowClose(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 60, display: 'flex', alignItems: 'flex-end' }}>
          <div onClick={e => e.stopPropagation()}
            style={{ width: '100%', background: 'var(--bg-elevated)', borderTopLeftRadius: 20, borderTopRightRadius: 20, padding: 20, maxHeight: '88vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ fontSize: 17, fontWeight: 800 }}>{t('waiter.closeTitle')}</h3>
              <button className="btn btn-ghost" onClick={() => setShowClose(false)} style={{ width: 'auto', flexShrink: 0, padding: '4px 8px' }}><Icon name="x" size={18} /></button>
            </div>

            {notReadyCount > 0 && (
              <div style={{ padding: 10, background: 'rgba(184,134,11,.12)', color: 'var(--warning, #b8860b)', borderRadius: 'var(--r-sm)', fontSize: 13, fontWeight: 600, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Icon name="alert" size={16} /> {t('waiter.notReadyWarn', { count: notReadyCount })}
              </div>
            )}

            {canDiscount && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 13, fontWeight: 700, color: 'var(--fg-muted)' }}>{t('waiter.discount')}</label>
                <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                  <button className={`btn btn-sm ${discType === 'percent' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setDiscType(discType === 'percent' ? null : 'percent')} style={{ width: 'auto', flexShrink: 0 }}>%</button>
                  <button className={`btn btn-sm ${discType === 'amount' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setDiscType(discType === 'amount' ? null : 'amount')} style={{ width: 'auto', flexShrink: 0 }}>{currency?.symbol || 'сум'}</button>
                  <input className="input" inputMode="decimal" value={discValue} onChange={e => setDiscValue(e.target.value)} placeholder="0" disabled={!discType} style={{ flex: 1 }} />
                </div>
              </div>
            )}

            {debtEnabled && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                  {t('waiter.onCredit')}
                  <input type="checkbox" checked={debtOn} onChange={e => setDebtOn(e.target.checked)} />
                </label>
                {debtOn && (
                  <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <select className="select" value={debtorId} onChange={e => setDebtorId(e.target.value)}>
                      <option value="">— {t('waiter.selectDebtor')} —</option>
                      {debtors.map(d => (
                        <option key={d.id} value={d.id} disabled={d.creditBlocked}>
                          {d.name}{d.creditBlocked ? ` — ${t('debtors.blacklistTag')}` : ''}
                        </option>
                      ))}

                    </select>
                    <div className="input-group" style={{ marginBottom: 0 }}>
                      <label>{t('waiter.paidNow')}</label>
                      <input className="input" inputMode="decimal" value={paidNow} onChange={e => setPaidNow(e.target.value)} placeholder={String(finalTotal)} />
                    </div>
                  </div>
                )}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontSize: 13, color: 'var(--fg-muted)' }}>{t('wholesale.total')}</span>
              <span style={{ fontSize: 13 }}>{formatMoney(base, currency)}</span>
            </div>
            {disc > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--danger)', marginBottom: 4 }}>
                <span>{t('waiter.discount')}</span><span>−{formatMoney(disc, currency)}</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '8px 0 14px' }}>
              <span style={{ fontSize: 15, fontWeight: 800 }}>{t('waiter.toPay')}</span>
              <span style={{ fontSize: 20, fontWeight: 800 }}>{formatMoney(finalTotal, currency)}</span>
            </div>
            {debtOn && credit > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--warning, #b8860b)', marginBottom: 12 }}>
                <span>{t('waiter.toCredit')}</span><span>{formatMoney(credit, currency)}</span>
              </div>
            )}

            <button className="btn btn-primary" onClick={submitClose} disabled={busy} style={{ width: '100%' }}>
              {t('waiter.closeConfirm', { total: formatMoney(finalTotal, currency) })}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
